import React from 'react';
import { UserCircle, X, ChevronRight } from 'lucide-react';
import { profileCompleteness } from '../hooks/useProfileNudge';

const s = {
  // Modal overlay
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1100, padding: '20px',
  },
  modal: {
    background: '#fff', borderRadius: '14px', padding: '28px',
    maxWidth: '400px', width: '100%', position: 'relative',
    boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
  },
  modalClose: {
    position: 'absolute', top: '14px', right: '14px',
    background: 'none', border: 'none', cursor: 'pointer',
    color: '#aaa', display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  iconWrap: {
    width: '44px', height: '44px', borderRadius: '12px',
    background: '#fef0e6', color: '#fd5f0d',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    marginBottom: '14px',
  },
  modalTitle:  { fontSize: '16px', fontWeight: '700', color: '#111', marginBottom: '4px' },
  modalSub:    { fontSize: '13px', color: '#777', marginBottom: '20px', lineHeight: '1.5' },
  input: {
    width: '100%', padding: '10px 12px', border: '1px solid #ddd',
    borderRadius: '8px', fontSize: '13px', marginBottom: '14px',
    boxSizing: 'border-box', outline: 'none',
  },
  btnRow:   { display: 'flex', gap: '8px' },
  saveBtn: {
    flex: 1, padding: '9px', background: '#fd5f0d', color: '#fff',
    border: 'none', borderRadius: '8px', fontSize: '13px',
    fontWeight: '600', cursor: 'pointer',
  },
  laterBtn: {
    padding: '9px 16px', background: 'none', color: '#888',
    border: '1px solid #e5e5e5', borderRadius: '8px', fontSize: '13px',
    cursor: 'pointer',
  },

  // Dashboard banner
  banner: {
    display: 'flex', alignItems: 'center', gap: '12px',
    background: '#fef0e6', border: '1px solid #ffcda6',
    borderRadius: '8px', padding: '10px 14px',
    marginBottom: '20px', fontSize: '13px',
  },
  bannerText: { flex: 1, color: '#b23e06' },
  bannerLink: {
    fontSize: '12px', fontWeight: '600', color: '#fd5f0d',
    background: 'none', border: 'none', cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: '2px', padding: 0,
  },
  bannerClose: {
    background: 'none', border: 'none', cursor: 'pointer',
    color: '#7b9fd4', display: 'flex', alignItems: 'center', padding: 0,
  },

  // Progress bar
  progressWrap: { height: '4px', background: '#ffd9c0', borderRadius: '2px', marginTop: '6px', width: '100%' },
  progressBar:  (pct) => ({ height: '4px', background: '#fd5f0d', borderRadius: '2px', width: `${pct}%`, transition: 'width 0.4s' }),
};

// ── Modal prompt for a single field ──────────────────────────────────────────
export function ProfileNudgeModal({ activeField, value, setValue, saving, onSave, onDismiss }) {
  if (!activeField) return null;

  return (
    <div style={s.overlay}>
      <div style={s.modal}>
        <button style={s.modalClose} onClick={onDismiss}><X size={18} strokeWidth={2} /></button>
        <div style={s.iconWrap}><UserCircle size={22} strokeWidth={1.75} /></div>
        <div style={s.modalTitle}>Complete your profile</div>
        <div style={s.modalSub}>
          Help the RAAH team reach you faster. What's your <strong>{activeField.label.toLowerCase()}</strong>?
        </div>
        <input
          style={s.input}
          type={activeField.type}
          placeholder={activeField.placeholder}
          value={value}
          onChange={e => setValue(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && onSave()}
          autoFocus
        />
        <div style={s.btnRow}>
          <button style={s.saveBtn} onClick={onSave} disabled={saving || !value.trim()}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button style={s.laterBtn} onClick={onDismiss}>Remind me later</button>
        </div>
      </div>
    </div>
  );
}

// ── Dashboard completion banner ───────────────────────────────────────────────
export function ProfileCompletionBanner({ profile, dismissed, onDismiss, onComplete }) {
  if (dismissed || !profile) return null;
  const pct = profileCompleteness(profile);
  if (pct === 100) return null;

  return (
    <div style={s.banner}>
      <UserCircle size={18} strokeWidth={1.75} color="#fd5f0d" />
      <div style={{ ...s.bannerText, flex: 1 }}>
        <div>Your profile is <strong>{pct}% complete</strong> — finishing it speeds up bookings and registrations.</div>
        <div style={s.progressWrap}><div style={s.progressBar(pct)} /></div>
      </div>
      <button style={s.bannerLink} onClick={onComplete}>
        Complete <ChevronRight size={13} strokeWidth={2.5} />
      </button>
      <button style={s.bannerClose} onClick={onDismiss}><X size={15} strokeWidth={2} /></button>
    </div>
  );
}

// ── Inline nudge for booking / workshop forms ─────────────────────────────────
export function ProfileInlineNudge({ fieldKey, fieldLabel, onComplete }) {
  return (
    <div style={{ ...s.banner, marginBottom: '12px', background: '#fffbe6', border: '1px solid #ffe066', borderRadius: '6px' }}>
      <UserCircle size={15} strokeWidth={1.75} color="#e67700" />
      <span style={{ ...s.bannerText, color: '#7a5f00', fontSize: '12px' }}>
        Add your <strong>{fieldLabel}</strong> to pre-fill this form automatically.
      </span>
      <button style={{ ...s.bannerLink, color: '#e67700' }} onClick={onComplete}>
        Add it <ChevronRight size={12} strokeWidth={2.5} />
      </button>
    </div>
  );
}